import React, {useEffect, useMemo, useState} from 'react';
import {t} from 'i18next';
import {Box, Container, Grid} from '@mui/material';
import ExplorerArrSourceSelectedContainer from '../explorer-component/ExplorerArrSourceSelectedContainer';
import FilterPanel, {
  ActiveFilter,
  FilterApplyPayload,
} from '../communication-explorer/FilterPanel';
import {useSaveConceptMapReportData} from '../../../hooks/conceptExplorere/useConceptExplorer';
import {
  initialToast,
  ReportNotificationType,
  ToastNotificationType,
} from './ConceptExplorer';
import ArrToastNotification from '../../common/arr-toast-notification/ArrToastNotification';
import {ToastType} from '../../../shared/constants';
import {useSelector} from 'react-redux';
import {RootState} from '../.../../../../redux/store';
import {IChild} from './heated-map-panel/TypesHeatMap';
import {useSearchParams} from 'react-router-dom';
import {useGetMetaDataFilter} from '../../../hooks/communicationExplorer/useCommunicationExplorer';
import {useDispatch} from 'react-redux';

export interface ConceptReportPayload {
  sourceName: string; // "SECURITY GROUPS/SEARCHES/FOLDERS"
  source: string[]; // e.g., ["1", "5", "16"]
  filters: FilterItem[];
  navigationSourceType: string; // "SECGROUP/SEARCH/FOLDER"
  redirectionData: string[] | null;
}

interface InputFilter {
  fieldID: string;
  fieldName: string;
  fieldType: string;
  literalValue: string;
}

interface OutputFilter {
  dataType: string;
  metaDataID: number;
  operand: string;
  projectFieldId: string;
  projectFieldLabel: string;
  projectFieldName: string;
  text: string;
  type: string;
  values: string[];
}

function parseLiteralValue(literalValue: string): {
  operand: string;
  values: string[];
} {
  if (!literalValue) {
    return {operand: '', values: []};
  }

  const bracketIndex = literalValue.indexOf('(');
  let operand =
    bracketIndex !== -1
      ? literalValue.substring(0, bracketIndex).trim().toUpperCase()
      : literalValue.trim().toUpperCase();
  operand = operand.replace(/\s+/g, '');

  const valuesPart =
    bracketIndex !== -1
      ? literalValue.substring(bracketIndex + 1, literalValue.lastIndexOf(')'))
      : '';

  const values = valuesPart
    .split(',')
    .map((v) =>
      v.trim().replace(/^N'/i, '').replace(/^'/, '').replace(/'$/, '')
    )
    .filter(Boolean);
  return {operand, values};
}

function convertToTargetFormat(input: InputFilter): OutputFilter {
  const {operand, values} = parseLiteralValue(input.literalValue);
  return {
    dataType: input.fieldType,
    metaDataID: 0,
    operand: operand,
    projectFieldId: input.fieldID,
    projectFieldLabel: input.fieldName,
    projectFieldName: input.fieldName,
    text: '',
    type: input.fieldType,
    values: values,
  };
}

export interface FilterItem {
  projectFieldId: string;
  projectFieldName: string;
  dataType: string;
  projectFieldLabel: string;
  type: string;
  operand: string;
  values: string[];
  metaDataID: number;
  text: string;
}

export type SourceItem = {
  id: string;
  label: string;
  data?: any[];
  [key: string]: any;
};

interface FilterHeaderProps {
  explorerData: any;
  onClearSourceId: () => void;
  setReqData: React.Dispatch<
    React.SetStateAction<ReportNotificationType | null>
  >;
  setCartItems: React.Dispatch<React.SetStateAction<IChild[]>>;
  setToastNotification: React.Dispatch<
    React.SetStateAction<ToastNotificationType>
  >;
}

const convertPayloadToSources = (
  explorerData: any,
  userRole: string
): SourceItem[] => {
  const data = explorerData?.data;
  if (!data) return [];
  // Base sources
  const baseSources: SourceItem[] = [
    {
      id: 'securityGroups',
      label: 'Security Groups',
      data: data.securityGroupList ?? [],
    },
    {
      id: 'searches',
      label: 'Searches',
      data: data.searchTreeList ?? [],
    },
    {
      id: 'folders',
      label: 'Folders',
      data: data.folderTreeList ?? [],
    },
  ];

  // Conditional item
  const conditionalItem: SourceItem =
    userRole === 'Review Manager'
      ? {
          id: 'assignments',
          label: 'Assignments',
          data: data.assignmentTreeList ?? [],
        }
      : {
          id: 'projects',
          label: 'Projects',
          data: data.projectList ?? [],
        };

  // Insert conditional item at index 2
  baseSources.splice(2, 0, conditionalItem);
  return baseSources;
};

const generateFilters = (
  activeFilters: ActiveFilter[],
  apiFilters: FilterItem[]
): FilterItem[] => {
  return activeFilters.map((item) => {
    const {id, selectionType, values, type, endDate, startDate} = item;
    const apiFilter = apiFilters.find((api) => api.projectFieldId === id);
    const operand: string =
      selectionType === 'include'
        ? 'IN'
        : selectionType === 'exclude'
          ? 'NOTIN'
          : (selectionType ?? 'IN');
    return {
      projectFieldId: apiFilter?.projectFieldId ?? id,
      projectFieldName: apiFilter?.projectFieldName ?? '',
      dataType: apiFilter?.dataType ?? '',
      projectFieldLabel: apiFilter?.projectFieldLabel ?? '',
      type: apiFilter?.dataType ?? '',
      metaDataID: apiFilter?.metaDataID ?? 0,
      text: apiFilter?.text ?? '',
      operand,
      values:
        type !== 'DATETIME'
          ? (values?.map((v) => v.text) ?? [])
          : [startDate ?? '', endDate ?? ''],
    };
  });
};

const FilterHeader: React.FC<FilterHeaderProps> = ({
  explorerData,
  setReqData,
  onClearSourceId,
  setCartItems,
  setToastNotification,
}) => {
  const [resetKey, setResetKey] = useState(0);
  const [showToast, setShowToast] = useState(false);
  const [selectedSourcesBySection, setSelectedSourcesBySection] = useState<
    Record<string, any[] | null>
  >({});
  const userRole = useSelector(
    (state: RootState) => state?.authenticatedUser?.user?.roleName
  );

  const {
    sourceId: communicationSourceId,
    sourceName,
    navigationSourceType,
  } = useSelector((state: RootState) => state.comExplorer);

  const {mutate: postConceptReport, isError} = useSaveConceptMapReportData();
  const [, setSearchParams] = useSearchParams();

  const sourceItems = useMemo(() => {
    return convertPayloadToSources(explorerData, userRole ?? '');
  }, [explorerData]);

  const resetSourceItems = () => {
    setReqData(null);
    setCartItems([]);
    setSearchParams({});
    setSelectedSourcesBySection({});
    setResetKey((prev) => prev + 1);
    setToastNotification(initialToast);
    onClearSourceId();
  };

  const _isSourceNotSelected = useMemo(() => {
    return communicationSourceId
      ? false
      : Object.values(selectedSourcesBySection).every((arr) => {
          if (arr == null) return true;
          if (Array.isArray(arr)) return arr.length === 0;
          return true;
        });
  }, [selectedSourcesBySection]);

  const {lstFilterSqlMetaData = []} = explorerData?.data || {};

  const {data: metaFilterData = null} = useGetMetaDataFilter({
    sourceTypeCode: 'conceptexplorer',
    enabled: !!lstFilterSqlMetaData.length,
  });

  const initSaveApiCall = (activeFilters: ActiveFilter[]) => {
    const selectedSource = Object.keys(selectedSourcesBySection)[0];
    const sourceDetails =
      selectedSource && selectedSourcesBySection[selectedSource]
        ? selectedSourcesBySection[selectedSource]![0]
        : null;
    const payload: ConceptReportPayload = {
      sourceName: sourceName ?? sourceDetails?.sourceName ?? '',
      source: communicationSourceId
        ? [communicationSourceId]
        : (sourceDetails?.source ?? []),
      navigationSourceType:
        navigationSourceType ?? sourceDetails?.navigationSourceType ?? '',
      filters: generateFilters(activeFilters, metaFilterData ?? []),
      redirectionData: null,
    };
    postConceptReport(payload, {
      onSuccess: (data = null) => {
        setReqData({
          ...data,
          generateConceptReportPayload: {
            sourceName: payload.sourceName,
            source: payload.source,
            filters: payload.filters,
            sourceType: payload.navigationSourceType,
            exportContextType: 'CLUSTERSUMMARYREPORT',
          },
        });
      },
      onError: () => {
        setReqData(null);
        setShowToast(true);
      },
    });
  };

  const handleApplyFilters = ({activeFilters}: FilterApplyPayload) => {
    if (_isSourceNotSelected) {
      setShowToast(true);
    } else {
      initSaveApiCall(activeFilters);
    }
  };
  useEffect(() => {
    if (explorerData?.conceptExplorerData?.sourceID) {
      setReqData({
        notificationId: 0,
        reportId: explorerData.reportId ?? 0,
        generateConceptReportPayload: {
          source: explorerData.conceptExplorerData.sourceID,
          sourceName: explorerData.conceptExplorerData.sourceType,
          sourceType: explorerData.conceptExplorerData.navigationSourceType,
          exportContextType: 'CLUSTERSUMMARYREPORT',
          filters: explorerData.conceptExplorerData.filterByMetaData.map(
            (item: InputFilter) => convertToTargetFormat(item)
          ),
        },
      });
    }
  }, [explorerData?.conceptExplorerData?.sourceID]);

  return (
    <Grid container>
      <Grid size={{sm: 6, md: 3}} className="explorer-grid">
        <Box sx={{padding: 2, height: '100%'}}>
          <ExplorerArrSourceSelectedContainer
            key={resetKey}
            sources={sourceItems}
            sourceID={explorerData?.conceptExplorerData?.sourceID ?? []}
            sourceType={explorerData?.conceptExplorerData?.sourceType ?? ''}
            onSelectionsUpdate={setSelectedSourcesBySection}
          />
        </Box>
      </Grid>

      <Grid size={{sm: 6, md: 9}} className="filters-container">
        <Container>
          <Box sx={{padding: 2}}>
            <FilterPanel
              dataAsOfProp={explorerData?.conceptExplorerData?.dataAsOf}
              title={t('docExplorer.filterAccordion.title')}
              filters={metaFilterData ?? []}
              onFilterApply={handleApplyFilters}
              selectedFilters={
                explorerData?.conceptExplorerData?.filterByMetaData
              }
              disableApply={_isSourceNotSelected}
              resetSourceItems={resetSourceItems}
            />
          </Box>
        </Container>
      </Grid>
      {showToast && (
        <ArrToastNotification
          message={
            isError
              ? t('filterPanel.toastFail')
              : _isSourceNotSelected
                ? t('filterPanel.selectSourceError')
                : ''
          }
          onClose={() => {
            setShowToast(false);
          }}
          status={[
            isError || _isSourceNotSelected
              ? ToastType.Error
              : ToastType.Success,
          ]}
        />
      )}
    </Grid>
  );
};

export default FilterHeader;
